
import React from 'react';
import { Droplets, Zap, Lightbulb, CheckCircle, ArrowRight, Battery, Settings } from 'lucide-react';

const ProductsPage: React.FC = () => {
  return (
    <div className="pb-24">
      {/* Header */}
      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-black text-slate-900 mb-6">Industrial Grade Solutions</h1>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg">
            Suryamitra designs custom solar systems using the latest Topcon DCR/Non-DCR modules for maximum efficiency.
          </p>
        </div>
      </section>

      {/* A. Solar Rooftop Categories */}
      <section className="py-24 container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-black mb-2 uppercase tracking-tight">Solar Rooftop Systems</h2>
          <p className="text-slate-500">For Residential, Commercial, Industrial, Institutions, Hotels & Resorts.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title: 'On-Grid Systems',
              desc: 'Best for day usage. Export excess power to BESCOM. Zero battery maintenance.',
              icon: <Zap className="text-yellow-600" />,
              features: ['Topcon DCR Modules', 'Net Metering Ready', 'Zero Energy Bills']
            },
            {
              title: 'Hybrid Systems',
              desc: 'Day & Night usage. Includes smart battery backup for zero power interruptions.',
              icon: <Battery className="text-blue-600" />,
              features: ['Lithium-ion/Tubular', 'Smart Inverter Tech', 'Night Power Export']
            },
            {
              title: 'Off-Grid Systems',
              desc: 'Complete independence. Ideal for resorts, farmhouses, and remote institutions.',
              icon: <Settings className="text-purple-600" />,
              features: ['Maximum Storage', 'Auto Generator Sync', '24/7 Energy Security']
            }
          ].map((type, i) => (
            <div key={i} className="p-10 bg-white rounded-[3rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all">
              <div className="mb-8 p-5 bg-slate-50 inline-block rounded-2xl">{type.icon}</div>
              <h3 className="text-2xl font-bold mb-4">{type.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-8">{type.desc}</p>
              <ul className="space-y-4">
                {type.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-3 text-xs font-black text-slate-400 uppercase tracking-widest">
                    <CheckCircle size={16} className="text-green-600" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* B. Irrigation Pumps */}
      <section className="py-24 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex p-4 bg-white text-green-700 rounded-2xl shadow-sm">
                <Droplets size={32} />
              </div>
              <h2 className="text-4xl font-black">Solar Irrigation Pumps</h2>
              <p className="text-lg text-green-900/70 leading-relaxed">
                Empowering horticulture and river lifting. Our systems are designed for high-lift, high-volume irrigation across Karnataka.
              </p>
              <div className="bg-white p-8 rounded-[2rem] border border-green-100">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Standard Pricing</div>
                <div className="text-4xl font-black text-green-800">₹50,000 / HP</div>
                <p className="text-xs text-slate-400 mt-4">Includes panel setup, pump installation, and testing.</p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1563514227147-6d2ff665a6a0?auto=format&fit=crop&q=80&w=1000" 
                className="rounded-[3rem] shadow-2xl" 
                alt="Solar Pump" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* C. Professional Lighting */}
      <section className="py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <img 
              src="https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?auto=format&fit=crop&q=80&w=1000" 
              className="rounded-[3rem] shadow-2xl" 
              alt="High Mast Lighting" 
            />
          </div>
          <div className="order-1 lg:order-2 space-y-8">
            <div className="inline-flex p-4 bg-orange-50 text-orange-600 rounded-2xl">
              <Lightbulb size={32} />
            </div>
            <h2 className="text-4xl font-black">Lighting & High Mast</h2>
            <p className="text-slate-600 leading-relaxed">
              Professional illumination for road safety and security. Custom-engineered based on road width, pole height, and required lumens.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <h4 className="font-bold text-slate-800 mb-2">Solar Streetlights</h4>
                <p className="text-slate-500 text-sm">Standalone units for gated communities and paths.</p>
              </div>
              <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                <h4 className="font-bold text-slate-800 mb-2">High Mast Systems</h4>
                <p className="text-slate-500 text-sm">Powerful arrays for junctions, factories, and squares.</p>
              </div>
            </div>
            <div className="text-xl font-bold text-slate-900 border-l-4 border-orange-400 pl-6">
               Price Range: <span className="text-orange-600">₹20,000 – ₹2,00,000</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
